import { RotateCcw } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { usePermission } from '../../../../auth/AuthContext';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { IndexPage } from '../../../../components/index-page/IndexPage';
import type { ColumnDef, IndexQueryState } from '../../../../components/index-page/types';
import type { MenuItem } from '../../../../components/Menu';
import { progressPercent } from '../../../../components/progressPercent';
import { StatusText } from '../../../../components/table/StatusText';
import { TextCell } from '../../../../components/table/TextCell';
import { useToast } from '../../../../components/toastContext';
import { ApiError } from '../../../../lib/apiError';
import { formatDateOnly } from '../../../../lib/formatDate';
import { attritionCategoriesApi } from '../../attrition-categories/attritionCategoriesApi';
import { usePersonNames, type PersonNames } from '../../personnel/usePersonNames';
import { approvalOf } from '../approvalProgress';
import { attritionApi } from '../attritionApi';
import {
  ATTRITION_REQUEST_AUDIT_ENTITY_TYPES,
  ATTRITION_STATUS,
  type AttritionRequestListItem,
} from '../attritionTypes';
import { AttritionRequestModal } from '../components/AttritionRequestModal';

/**
 * Human Resources › Attrition › <category> — the separations filed under ONE category. The
 * sidebar lists one entry per category (product call, 31 Oct 2026), and the route names it, so
 * the category is applied to every fetch and is neither a facet nor a column here: both would say
 * the same thing on every row. The folder nav is the STAGE.
 *
 * **The employee's NAME is resolved for the page on screen**, in one batch through the identity
 * service — a row carries a personId and no name. The column is gated on `personnel.read`, and a
 * row above the reader's clearance arrives with no personId at all and reads "Restricted".
 *
 * **"Awaiting my review" is a VIEW SCOPE in the cog**, not a facet: whether a document waits on
 * you is a property of who is asking, and no row can be filtered by it. Not persisted, like
 * Show deleted.
 */
export function AttritionRequestsIndexPage() {
  const { categoryId = '' } = useParams<{ categoryId: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const canReadPersonnel = usePermission('personnel.read');
  const canReview = usePermission('attrition-requests.review');

  const [creating, setCreating] = useState(false);
  const [awaitingMe, setAwaitingMe] = useState(false);
  const [confirming, setConfirming] = useState<{ kind: 'delete' | 'restore'; row: AttritionRequestListItem } | null>(
    null,
  );
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const [pageIds, setPageIds] = useState<string[]>([]);
  const names = usePersonNames(canReadPersonnel ? pageIds : []);

  /*
    THE CATEGORY'S NAME, for the breadcrumb, off the register's own feed (the sidebar's) — a reader
    of requests may hold no catalog key. Null until it arrives; the crumb waits rather than guesses.
  */
  const [loadedName, setLoadedName] = useState<{ categoryId: string; name: string | null } | null>(null);

  useEffect(() => {
    const controller = new AbortController();

    attritionApi
      .categories(controller.signal)
      .then((categories) => {
        if (!controller.signal.aborted) {
          setLoadedName({ categoryId, name: categories.find((c) => c.id === categoryId)?.name ?? null });
        }
      })
      .catch(() => undefined);

    return () => controller.abort();
  }, [categoryId]);

  // DERIVED during render: a name loaded for the PREVIOUS category is never shown on this one.
  const categoryName = loadedName?.categoryId === categoryId ? loadedName.name : null;

  /*
    WHAT A REQUEST CANNOT BE FILED WITHOUT — THIS category's checklist holding a document. Seeded
    TRUE, and a failed feed (or a reader without the catalog key) leaves it true: a request that did
    not come back is not evidence that the list is empty.
  */
  const [hasChecklist, setHasChecklist] = useState(true);

  useEffect(() => {
    const controller = new AbortController();

    attritionCategoriesApi
      .options(controller.signal)
      .then((rows) => {
        if (!controller.signal.aborted) {
          setHasChecklist((rows.find((c) => c.id === categoryId)?.documents.length ?? 0) > 0);
        }
      })
      .catch(() => undefined);

    return () => controller.abort();
  }, [categoryId, reloadKey]);

  const refresh = useCallback(() => setReloadKey((k) => k + 1), []);

  const fetchRows = useCallback(
    async (query: IndexQueryState, signal: AbortSignal) => {
      const page = await attritionApi.list(
        {
          search: query.search,
          page: query.page,
          pageSize: query.pageSize,
          sort: query.sort ?? undefined,
          status: query.facets.status?.join(','),
          stage: query.facets.stage?.join(','),
          // The ROUTE's category, on every fetch — this page is that category's register.
          category: categoryId,
          awaitingMe,
        },
        signal,
      );

      // Only the ids that came back; `!= null` because an absent one is omitted from the wire.
      setPageIds(page.items.map((r) => r.personId).filter((id): id is string => id != null));

      return page;
    },
    [awaitingMe, categoryId],
  );

  const columns = useMemo(() => buildColumns(names), [names]);

  const rowActions = useCallback(
    (row: AttritionRequestListItem): MenuItem[] => [
      {
        key: `restore-${row.id}`,
        label: 'Restore',
        icon: <RotateCcw className="h-4 w-4" aria-hidden="true" />,
        permission: 'attrition-requests.delete',
        disabledReason: row.isActive ? 'This request is already active.' : null,
        onClick: () => setConfirming({ kind: 'restore', row }),
      },
    ],
    [],
  );

  async function runConfirmed() {
    if (!confirming) return;

    setRunning(true);
    setError(null);

    try {
      if (confirming.kind === 'delete') await attritionApi.remove(confirming.row.id);
      else await attritionApi.restore(confirming.row.id);

      toast.success(`${confirming.row.requestNumber} was ${confirming.kind === 'delete' ? 'deleted' : 'restored'}.`);
      setConfirming(null);
      refresh();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : new ApiError('The action could not be completed.', 0));
    } finally {
      setRunning(false);
    }
  }

  return (
    <>
      <IndexPage<AttritionRequestListItem>
        // A new category is a new register: the shell's paging and facets start over.
        key={categoryId}
        module="attrition-requests"
        breadcrumb={['Human Resources', 'Attrition', categoryName ?? 'Requests']}
        columns={columns}
        facets={[
          { key: 'status', label: 'Status' },
          { key: 'stage', label: 'Stage' },
        ]}
        // Filed by where each separation stands — the category is already the page.
        folderNavFacet="stage"
        recordFilters={
          canReview
            ? [
                {
                  key: 'attrition-scope-awaiting-me',
                  label: 'Awaiting my review',
                  checked: awaitingMe,
                  permission: 'attrition-requests.review',
                  onClick: () => setAwaitingMe((current) => !current),
                },
              ]
            : undefined
        }
        auditEntityTypes={ATTRITION_REQUEST_AUDIT_ENTITY_TYPES}
        rowKey={(row) => row.id}
        fetch={fetchRows}
        reloadKey={reloadKey}
        searchPlaceholder="Search by request number"
        emptyMessage={
          awaitingMe
            ? 'Nothing under this category is waiting on your review or your clearance.'
            : `No ${categoryName ?? 'attrition'} request yet.`
        }
        prerequisites={[
          {
            // Read into "Set up … first" / "Nothing has been added to … yet" / "Go to …".
            label: 'this category\'s checklist',
            met: hasChecklist,
            to: `/attrition/settings/categories/${categoryId}`,
            permission: 'attrition-categories.read',
          },
        ]}
        onCreate={() => setCreating(true)}
        createPermission="attrition-requests.create"
        onRowOpen={(row) => navigate(`/attrition/requests/${row.id}`)}
        openPermission="attrition-requests.read"
        rowActions={rowActions}
        onDelete={(row) => setConfirming({ kind: 'delete', row })}
        deletePermission="attrition-requests.delete"
        deleteLabel={() => 'Delete'}
        deleteDisabledReason={(row) =>
          !row.isActive
            ? 'This request is already deleted.'
            : row.status === ATTRITION_STATUS.completed
              ? 'A completed request is the record of the separation and cannot be deleted.'
              : null
        }
        /*
          FULFILMENT MONITORING — approved documents over the checklist. A cancelled request is
          outside the question, so it carries no ring rather than a stalled one.
        */
        rowProgress={(row) => {
          const progress = approvalOf(row);

          return progress ? { value: progress.done, total: progress.total, label: 'documents approved and units cleared' } : null;
        }}
        otherInfo={(row) => row}
      />

      {creating ? (
        <AttritionRequestModal
          open
          editing={null}
          defaultCategoryId={categoryId}
          onClose={() => setCreating(false)}
          onSaved={(saved, options) => {
            // Add another keeps the form up and the register fresh; otherwise go to the new request.
            if (options?.keepOpen) {
              refresh();
              return;
            }
            setCreating(false);
            navigate(`/attrition/requests/${saved.id}`);
          }}
        />
      ) : null}

      {confirming ? (
        <ConfirmDialog
          open
          onClose={() => {
            setConfirming(null);
            setError(null);
          }}
          onConfirm={() => void runConfirmed()}
          title={confirming.kind === 'delete' ? 'Delete request' : 'Restore request'}
          danger={confirming.kind === 'delete'}
          requireConfirm={confirming.kind === 'delete'}
          requireCredential={confirming.kind === 'delete'}
          loading={running}
          id={`attrition-request-${confirming.kind}-dialog`}
          body={
            <div className="flex flex-col gap-2">
              {confirming.kind === 'delete' ? (
                <>
                  <p>{confirming.row.requestNumber} is hidden from the register.</p>
                  <p>
                    <strong>This is not the same as cancelling it.</strong> Deleting is for a request filed against
                    the wrong employee, or twice. If the separation will not go forward, cancel it from its own page —
                    that is an outcome the register keeps.
                  </p>
                  <p>Nothing is erased — the documents stay, and a deleted request can be restored from this list.</p>
                </>
              ) : (
                <p>{confirming.row.requestNumber} returns to the register where it was.</p>
              )}

              {error ? (
                <p role="alert" className="rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700">
                  {error.message}
                </p>
              ) : null}
            </div>
          }
        />
      ) : null}
    </>
  );
}

/** Built per render because the employee column reads the resolved names. */
function buildColumns(names: PersonNames): ColumnDef<AttritionRequestListItem>[] {
  return [
    {
      key: 'requestNumber',
      header: 'Request',
      width: 170,
      sortable: true,
      render: (row) => <TextCell value={row.requestNumber} mono />,
    },
    {
      key: 'employee',
      header: 'Employee',
      width: 240,
      summary: true,
      permission: 'personnel.read',
      render: (row) => (
        <TextCell
          value={
            row.personId == null
              ? 'Restricted'
              : names.statusOf(row.personId) === 'erased'
                ? 'Identity erased'
                : (names.nameOf(row.personId) ?? (names.failed ? 'Name unavailable' : null))
          }
        />
      ),
    },
    {
      key: 'employeeNumber',
      header: 'Employee no.',
      width: 160,
      permission: 'personnel.read',
      render: (row) => <TextCell value={row.employeeNumber} mono />,
    },
    {
      key: 'effectiveDate',
      header: 'Effective',
      width: 140,
      sortable: true,
      summary: true,
      // A calendar date, never the instant path — <DateCell> is the wrong component for one.
      render: (row) => <TextCell value={row.effectiveDate != null ? formatDateOnly(row.effectiveDate) : null} mono />,
    },
    {
      /*
        THE TABLE OWES THE SAME PERCENTAGE THE RING STATES, off the same derivation. An em dash
        where there is nothing to be a fraction of — a cancelled request.
      */
      key: 'progress',
      header: 'Progress',
      width: 110,
      align: 'right',
      render: (row) => {
        const progress = approvalOf(row);

        return <TextCell value={progress ? `${progressPercent(progress.done, progress.total)}%` : '—'} mono numeric />;
      },
    },
    {
      key: 'stage',
      header: 'Stage',
      width: 170,
      sortable: true,
      stage: true,
      render: (row) => <StatusText tone="inactive">{row.statusLabel}</StatusText>,
    },
    {
      key: 'status',
      header: 'Status',
      width: 120,
      status: true,
      render: (row) =>
        row.isActive ? <StatusText tone="active">Active</StatusText> : <StatusText tone="inactive">Deleted</StatusText>,
    },
  ];
}
